import React, {
  useState,
  useCallback,
  useEffect,
  SetStateAction,
  Dispatch,
} from "react";
import {
  CreateBidRuvnlTable,
  fetchModRuvnlTableData,
  ModRuvnlUpdateData,
} from "../../Utility/RestApi";
import { useSelector } from "react-redux";
import { SearchBox } from "../../../../components/Search";
import { ArrowLeft } from "lucide-react";
import loader from "../../../../components/recharts/loader.gif";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";
interface Generator {
  ownership: any;
  generator_type: any;
  date: string;
  generator: string;
  margin_aggressive_sell: number;
  margin_buy: number;
  margin_conservative_sell: number;
  must_run: number;
  time_slot__slot: number;
  total_capacity: number;
  vc: number;
}

interface SliderCellProps {
  value: number;
  onChange: (value: string) => void;
  min?: number;
  max: number;
  step?: number;
  unit?: string;
}

interface UserModifications {
  [generatorName: string]: {
    [timeSlot: number]: {
      vc?: number;
      must_run?: number;
      margin_buy?: number;
      margin_conservative_sell?: number;
      margin_aggressive_sell?: number;
    };
  };
}

const SliderCell: React.FC<SliderCellProps> = React.memo(
  ({ value, onChange, min = 0, max = 10, step = 0.1, unit = "" }) => (
    <div className="flex flex-col items-center space-y-2 p-2">
      <div className="text-xs font-medium text-[#34656D]">
        {value.toFixed(3)}
        {unit}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-24 h-2 bg-[#F1935C] rounded-lg appearance-none cursor-pointer custom-slider"
        style={{
          background: `linear-gradient(to right, #F1935C 0%, #F1935C ${
            ((value - min) / (max - min)) * 100
          }%, #F4F7FB ${((value - min) / (max - min)) * 100}%, #F4F7FB 100%)`,
        }}
      />
    </div>
  )
);

export default function SimulationTable({
  open,
  setOpen,
  setError,
  refreshKey,
  setRefreshKey,
  selectedBlock,
  setSelectedBlock,
  selectedGeneratorName,
  setSelectedGeneratorName,
}: {
  open: boolean;
  setOpen: any;
  setError?: any;
  refreshKey: any;
  setRefreshKey: any;
  selectedBlock: any[];
  setSelectedBlock: Dispatch<SetStateAction<number[]>>;
  // setSelectedBlock: (value: any[]) => void;
  selectedGeneratorName: any[];
  setSelectedGeneratorName: (value: any[]) => void;
}) {
  const [sortBy, setSortBy] = useState<string>("generator_name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const preSelectedDate = useSelector(
    (state: any) => state.modRuvnlDate.selectedDate
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modRuvnlSession.modSession
  );
  const [fullbtn, setFullBtn] = useState(false);
  const [data, setData] = useState<Generator[]>([]);
  const [uniqueGeneratorName, setUniqueGeneratorName] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modData, setModData] = useState<Generator[]>([]);
  const [originalData, setOriginalData] = useState<Generator[]>([]);
  const [userModifications, setUserModifications] = useState<UserModifications>(
    {}
  );

  function handleSort(field: string): void {
    if (!["total_capacity", "vc"].includes(field)) return; // ✅ only allow sorting on DC

    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }

    setData((data) => {
      const sorted = [...data].sort((a, b) => {
        let aValue = (a as any)[field];
        let bValue = (b as any)[field];
        aValue = Number(aValue);
        bValue = Number(bValue);
        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
      return sorted;
    });
  }

  const updateGeneratorValue = useCallback(
    (generatorIndex: number, field: keyof Generator, value: string): void => {
      const numValue = parseFloat(value);
      const generatorName = modData[generatorIndex]?.generator;
      const timeSlot = modData[generatorIndex]?.time_slot__slot;

      if (!generatorName || timeSlot === undefined) return;

      // If single block is selected, update that specific block
      if (selectedBlock.length === 1) {
        const blockNumber = Number(selectedBlock[0].value ?? selectedBlock[0]);
        setUserModifications((prev) => ({
          ...prev,
          [generatorName]: {
            ...prev[generatorName],
            [blockNumber]: {
              ...prev[generatorName]?.[blockNumber],
              [field]: numValue,
            },
          },
        }));
      } else {
        // If multiple blocks or no blocks selected, we need to update all relevant blocks
        // Get all blocks that would be affected by this generator
        let blocksToUpdate: number[] = [];

        if (selectedBlock.length > 0) {
          blocksToUpdate = selectedBlock.map((b: any) => Number(b.value ?? b));
        } else {
          // If no blocks selected, get all blocks for this generator
          blocksToUpdate = Array.from(
            new Set(
              data
                .filter((item) => item.generator === generatorName)
                .map((item) => item.time_slot__slot)
            )
          );
        }

        setUserModifications((prev) => {
          const newModifications = { ...prev };
          if (!newModifications[generatorName]) {
            newModifications[generatorName] = {};
          }

          blocksToUpdate.forEach((block) => {
            newModifications[generatorName][block] = {
              ...newModifications[generatorName][block],
              [field]: numValue,
            };
          });

          return newModifications;
        });
      }

      // Update the modData immediately for UI responsiveness
      setModData((prev) =>
        prev.map((gen, index) =>
          index === generatorIndex ? { ...gen, [field]: numValue } : gen
        )
      );
    },
    [modData, selectedBlock, data]
  );

  const handleUpdateReview = useCallback(
    async (data: any) => {
      try {
        setLoading(true);
        await ModRuvnlUpdateData({
          date: preSelectedDate,
          session: modSelectedSession,
          data: data,
        });

        await CreateBidRuvnlTable({
          date: preSelectedDate,
          session: modSelectedSession,
        });
        setRefreshKey((prev: number) => prev + 1);
        setUserModifications({});
        setOpen(true);
      } catch (error) {
        setError("Failed to update Simulation data");
        console.error("Error updating Simulation data:", error);
      } finally {
        setLoading(false);
      }
    },
    [preSelectedDate, modSelectedSession, setRefreshKey, setOpen, setError]
  );

  const resetValues = useCallback(() => {
    setUserModifications({});
    setModData([...originalData]);
  }, [originalData]);

  const handleFetchData = useCallback(async () => {
    if (!preSelectedDate || !modSelectedSession) return;

    try {
      setLoading(true);
      const res = await fetchModRuvnlTableData(
        preSelectedDate,
        modSelectedSession
      );
      setData(res.data);
      setUniqueBlock(
        Array.from(
          new Set(
            res.data.map(
              (item: { time_slot__slot: any }) => item.time_slot__slot
            )
          )
        )
      );
      const extraGenerator = "DEFICIT";
      setUniqueGeneratorName(
        Array.from(
          new Set([
            ...res.data.map((item: { generator: any }) => item.generator),
            extraGenerator,
          ])
        )
      );
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  }, [modSelectedSession, preSelectedDate, setError]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  useEffect(() => {
    let filtered: Generator[] = data;

    if (selectedGeneratorName.length > 0) {
      const generator = selectedGeneratorName.map((o: any) => o.value ?? o);
      filtered = filtered.filter((row) => generator.includes(row.generator));
    }

    if (selectedBlock.length > 0) {
      const blocks = selectedBlock.map((b: any) => Number(b.value ?? b));
      filtered = filtered.filter((row) =>
        blocks.includes(Number(row.time_slot__slot))
      );
    }

    // If single block is selected, show actual data for that block with user modifications
    if (selectedBlock.length === 1) {
      const blockNumber = Number(selectedBlock[0].value ?? selectedBlock[0]);
      const modifiedData = filtered.map((item) => {
        const modifications =
          userModifications[item.generator]?.[item.time_slot__slot];
        if (modifications) {
          return {
            ...item,
            ...modifications,
          };
        }
        return item;
      });
      setModData(modifiedData);
      setOriginalData([...filtered]);
    } else {
      // If multiple blocks are selected or no blocks are selected, group by generator name and calculate average
      const groupedData: { [key: string]: Generator[] } = {};
      filtered.forEach((item) => {
        if (!groupedData[item.generator]) {
          groupedData[item.generator] = [];
        }
        groupedData[item.generator].push(item);
      });

      // Create aggregated data with averages for all fields
      const aggregatedData: Generator[] = Object.keys(groupedData).map(
        (generatorName) => {
          const generatorGroup = groupedData[generatorName];

          // Helper function to calculate average with user modifications
          const calculateAverageWithModifications = (
            field: keyof Generator
          ) => {
            let sum = 0;
            let count = 0;

            generatorGroup.forEach((item) => {
              const userMod =
                userModifications[generatorName]?.[item.time_slot__slot]?.[
                  field as keyof UserModifications[string][number]
                ];
              const value =
                userMod !== undefined ? userMod : (item[field] as number);
              sum += value;
              count++;
            });

            return sum / count;
          };

          // Calculate averages for all numeric fields with user modifications applied
          const avgTotalCapacity =
            calculateAverageWithModifications("total_capacity");
          const avgVc = calculateAverageWithModifications("vc");
          const avgMustRun = calculateAverageWithModifications("must_run");
          const avgMarginBuy = calculateAverageWithModifications("margin_buy");
          const avgMarginConservativeSell = calculateAverageWithModifications(
            "margin_conservative_sell"
          );
          const avgMarginAggressiveSell = calculateAverageWithModifications(
            "margin_aggressive_sell"
          );

          // Use the first item as base and override with averages
          return {
            ...generatorGroup[0],
            total_capacity: avgTotalCapacity,
            vc: avgVc,
            must_run: avgMustRun,
            margin_buy: avgMarginBuy,
            margin_conservative_sell: avgMarginConservativeSell,
            margin_aggressive_sell: avgMarginAggressiveSell,
          };
        }
      );

      setModData(aggregatedData);
      setOriginalData([...aggregatedData]);
    }
  }, [data, selectedBlock, selectedGeneratorName, userModifications]);

  // Function to get the final data for API submission - only modified records
  const getFinalDataForSubmission = useCallback(() => {
    let modifiedData: Generator[] = [];

    // Get the base data (all data, not filtered)
    const baseData: Generator[] = data;

    // Only include records that have been modified
    Object.keys(userModifications).forEach((generatorName) => {
      Object.keys(userModifications[generatorName]).forEach((timeSlotStr) => {
        const timeSlot = Number(timeSlotStr);
        const modifications = userModifications[generatorName][timeSlot];

        // Find the original record for this generator and time slot
        const originalRecord = baseData.find(
          (item) =>
            item.generator === generatorName &&
            item.time_slot__slot === timeSlot
        );

        if (originalRecord && modifications) {
          // Create modified record with only the changed fields
          const modifiedRecord = {
            ...originalRecord,
            ...modifications,
          };
          modifiedData.push(modifiedRecord);
        }
      });
    });

    return modifiedData;
  }, [data, userModifications]);

  const handleApplyChanges = useCallback(() => {
    const finalData = getFinalDataForSubmission();
    handleUpdateReview(finalData);
  }, [getFinalDataForSubmission, handleUpdateReview]);

  const hasModifications =
    Object.keys(userModifications).length > 0 &&
    Object.values(userModifications).some(
      (generator) => Object.keys(generator).length > 0
    );

  return (
    <>
      <div
        className="gas-chart-container simulation_table shadow-[rgba(149,157,165,0.2)_0px_8px_24px]"
        id="slide"
        style={{
          width: open ? "28.5%" : "81%",
          position: open ? "absolute" : "absolute",
          zIndex: "2",
          height: "calc(-195px + 100vh)",
          transition: "all 0.8s ease-out",
          paddingRight: open ? "1rem" : "12px",
        }}
      >
        <button
          style={{
            width: fullbtn ? "100px" : "30px",
            overflow: "hidden",
            height: "30px",
            right: "0px",
          }}
          onMouseOver={() => setFullBtn(true)}
          onMouseLeave={() => setFullBtn(false)}
          onClick={() => setOpen(!open)}
          className="hide-btn flex items-center justify-between gap-1 transition-all text-nowrap duration-700 ease-in-out "
        >
          <ArrowLeft
            size={18}
            className={`text-primary transition-transform duration-300 ${
              !open ? "rotate-359" : "rotate-180"
            }`}
          />
          {fullbtn && (
            <>{open ? <span>Show Filters</span> : <span>Hide Filters</span>}</>
          )}
        </button>
        <div>
          <div className="flex justify-between items-center mb-2 border-b border-[#DFEAF2] pb-2">
            {/* <h2 className="chartHeading w-60">Simulation Console</h2> */}
            <div className="flex items-center gap-3">
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Select Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <SearchBox
                options={uniqueGeneratorName.map((item) => ({
                  value: item,
                  label: item,
                }))}
                isMany={true}
                placeholder="Select Generator"
                selectedOptions={selectedGeneratorName}
                onChange={setSelectedGeneratorName}
              />
              {/* <SearchBox
                options={uniqueBlock.map((item) => ({
                  value: item,
                  label: item,
                }))}
                isMany={true}
                placeholder="Select Block"
                selectedOptions={selectedBlock}
                onChange={setSelectedBlock}
              /> */}
              {open ? (
                <div className="flex gap-x-2 justify-end">
                  <button
                    disabled={loading || !hasModifications}
                    onClick={handleApplyChanges}
                    className={`h-[38px] gap-2 text-sm rounded-xl disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#F1935C] text-white font-medium w-[85px]`}
                  >
                    {loading ? "Loading" : "Apply"}
                  </button>
                </div>
              ) : (
                <></>
              )}
            </div>
            {open ? (
              <> </>
            ) : (
              <div className="flex gap-x-2 justify-end">
                <button
                  onClick={resetValues}
                  className="w-[78px] h-[32px] gap-2 text-sm rounded-xl disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#FFFFF] text-[#6F6D7C] font-medium"
                >
                  Reset
                </button>
                <button
                  disabled={loading || !hasModifications}
                  onClick={handleApplyChanges}
                  className={`h-[38px] gap-2 text-sm rounded-xl disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#F1935C] text-white font-medium ${
                    loading ? "w-[120px]" : "w-[100px]"
                  }`}
                >
                  {loading ? "Loading..." : "Apply"}
                </button>
              </div>
            )}
          </div>

          <div className="chartTable h-100">
            {loading ? (
              <div className="h-full w-full bg-white flex items-center justify-center  border-none">
                <img
                  src={loader}
                  width={200}
                  alt="loader"
                  className="block mt-[5rem]"
                />
              </div>
            ) : (
              <div
                className={`capitalize text-nowrap rounded-lg border border-gray-200 ${
                  open ? "overflow-hidden overflow-y-auto" : "overflow-auto"
                }`}
                style={{ maxHeight: "calc(-270px + 100vh)" }}
              >
                <style>{`
          .custom-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            height: 16px;
            width: 16px;
            border-radius: 50%;
            background: #F1935C;
            cursor: pointer;
            border: 2px solid #ffffff;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
            margin-top: 0px;
          }
          
          .custom-slider::-moz-range-thumb {
            height: 16px;
            width: 16px;
            border-radius: 50%;
            background: #F1935C;
            cursor: pointer;
            border: 2px solid #ffffff;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }
        `}</style>

                <table id="my-table" className="table-auto">
                  <thead className="sticky top-0 z-50 text-[#F1935C] bg-[#FAFAFA]">
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-center font-semibold text-[#525062]">
                        Generator Name
                      </th>
                      <th
                        onClick={() => handleSort("total_capacity")}
                        className="text-center font-semibold text-[#525062]"
                      >
                        {open ? "DC (MW)" : "Declared Capacity (MW)"}
                        {sortBy === "total_capacity" &&
                          (sortOrder === "asc" ? "▲" : "▼")}
                      </th>
                      <th
                        onClick={() => handleSort("vc")}
                        className="text-center font-semibold text-[#525062]"
                      >
                        {open ? "VC (₹/kWh)" : " Variable Charge (₹/kWh)"}
                        {sortBy === "vc" && (sortOrder === "asc" ? "▲" : "▼")}
                      </th>
                      <th className="text-center font-semibold text-[#525062]">
                        Must Run (%)
                      </th>
                      <th className="text-center font-semibold text-[#525062]">
                        Buy Margin (₹/kWh)
                      </th>
                      <th className="text-center font-semibold text-[#525062]">
                        Sell Margin (₹/kWh)
                      </th>
                      <th className="text-center font-semibold text-[#525062]">
                        Sell Margin Aggressive (₹/kWh)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {modData.map((item, index) => (
                      <tr
                        key={`${item.generator}-${item.time_slot__slot}-${index}`}
                        className="border-b border-gray-100 hover:bg-gray-50/70 transition-colors duration-150"
                      >
                        <td className="font-medium text-[#8C8A96] text-sm">
                          {item.generator}
                        </td>
                        <td className="text-center font-medium text-[#F1935C] text-sm">
                          {item.total_capacity.toFixed(2)}
                        </td>
                        <td className="text-center">
                          <SliderCell
                            value={item.vc}
                            onChange={(value) =>
                              updateGeneratorValue(index, "vc", value)
                            }
                            min={0}
                            max={10}
                            step={0.001}
                          />
                        </td>
                        <td className="text-center">
                          <SliderCell
                            value={item.must_run}
                            onChange={(value) =>
                              updateGeneratorValue(index, "must_run", value)
                            }
                            min={0}
                            max={100}
                            step={0.1}
                          />
                        </td>
                        <td className="text-center">
                          <SliderCell
                            value={item.margin_buy}
                            onChange={(value) =>
                              updateGeneratorValue(index, "margin_buy", value)
                            }
                            min={0}
                            max={10}
                            step={0.001}
                          />
                        </td>
                        <td className="text-center">
                          <SliderCell
                            value={item.margin_conservative_sell}
                            onChange={(value) =>
                              updateGeneratorValue(
                                index,
                                "margin_conservative_sell",
                                value
                              )
                            }
                            min={0}
                            max={10}
                            step={0.001}
                          />
                        </td>
                        <td className="text-center">
                          <SliderCell
                            value={item.margin_aggressive_sell}
                            onChange={(value) =>
                              updateGeneratorValue(
                                index,
                                "margin_aggressive_sell",
                                value
                              )
                            }
                            min={0}
                            max={10}
                            step={0.001}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
